import { LANGUAGE_TAGS, type LanguageTag } from "@growthwise/core";
import { en, type Messages } from "./en";
import { zhCN } from "./zh-CN";

export type { Messages };
export { LANGUAGE_TAGS };
export type { LanguageTag };

export const CATALOGS: Record<LanguageTag, Messages> = {
  en,
  "zh-CN": zhCN,
};

export const DEFAULT_LANGUAGE: LanguageTag = "en";

/** Locale preference key. Scoped so it cannot collide with other state. */
export const LANGUAGE_STORAGE_KEY = "growthwise.language";

/** The `lang` attribute and `hreflang` value for each catalogue. */
export const HTML_LANG: Record<LanguageTag, string> = {
  en: "en",
  "zh-CN": "zh-Hans",
};

export const LANGUAGE_LABELS: Record<LanguageTag, string> = {
  en: "English",
  "zh-CN": "简体中文",
};

export const isSupportedLanguage = (value: unknown): value is LanguageTag =>
  typeof value === "string" &&
  (LANGUAGE_TAGS as readonly string[]).includes(value);

/**
 * Splits a pathname into its language segment and the rest.
 *
 * Every page lives under an explicit language prefix (`/en/...`,
 * `/zh-CN/...`) so that each translation has its own addressable URL that
 * can be linked, bookmarked and indexed independently.
 */
export const splitLocalePath = (
  pathname: string,
): { language: LanguageTag | null; rest: string } => {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const [, first = "", ...others] = normalized.split("/");

  if (isSupportedLanguage(first)) {
    const rest = `/${others.join("/")}`.replace(/\/+$/, "");
    return { language: first, rest: rest === "" ? "/" : rest };
  }
  return { language: null, rest: normalized };
};

/**
 * Paths that need the auth SDK immediately: sign-in, the applicant portal,
 * the recorder and admin. Public marketing pages delay that download.
 */
const PORTAL_PATH_PREFIXES = [
  "/sign-in",
  "/sign-up",
  "/verify-email",
  "/reset-password",
  "/account",
  "/admin",
  "/video-introduction",
  "/admin-setup",
] as const;

export const isPortalPath = (pathname: string): boolean => {
  const { rest } = splitLocalePath(pathname);
  return PORTAL_PATH_PREFIXES.some(
    (prefix) => rest === prefix || rest.startsWith(`${prefix}/`),
  );
};

/** Builds a URL for `path` in `language`. `path` is always language-neutral. */
export const localizedPath = (language: LanguageTag, path: string): string => {
  const clean = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
  return `/${language}${clean}`;
};

/**
 * Chooses a language for a visitor with no explicit URL language.
 *
 * Order: a previously saved preference, then the browser's Accept-Language
 * list, then English. A stored preference wins because it is an explicit
 * choice the visitor already made.
 */
export const detectLanguage = (
  stored: string | null,
  browserLanguages: readonly string[],
): LanguageTag => {
  if (isSupportedLanguage(stored)) return stored;

  for (const candidate of browserLanguages) {
    const lower = candidate.toLowerCase();
    if (lower === "zh-cn" || lower === "zh" || lower.startsWith("zh-hans"))
      return "zh-CN";
    // Other Chinese scripts (zh-TW, zh-HK) are Traditional; this site only
    // ships Simplified, so they fall through to English rather than being
    // served text in the wrong script.
    if (lower.startsWith("en")) return "en";
  }
  return DEFAULT_LANGUAGE;
};

/**
 * Looks up a dot-separated key and substitutes `{placeholder}` values.
 *
 * A missing key returns the key itself rather than throwing or rendering
 * blank, so a translation gap is visible in review instead of producing an
 * empty page.
 */
export const translate = (
  messages: Messages,
  key: string,
  values?: Record<string, string | number>,
): string => {
  const resolved = resolveKey(messages, key);
  if (typeof resolved !== "string") return key;
  return values ? interpolate(resolved, values) : resolved;
};

export const translateList = (messages: Messages, key: string): string[] => {
  const resolved = resolveKey(messages, key);
  if (!Array.isArray(resolved)) return [];
  return resolved.filter((entry): entry is string => typeof entry === "string");
};

const resolveKey = (messages: Messages, key: string): unknown => {
  let current: unknown = messages;
  for (const segment of key.split(".")) {
    if (typeof current !== "object" || current === null) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
};

export const interpolate = (
  template: string,
  values: Record<string, string | number>,
): string =>
  template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];
    return value === undefined ? match : String(value);
  });

/**
 * Formats a byte count for display.
 *
 * Locale-aware number formatting matters here: Chinese and English group
 * digits the same way, but the unit spacing convention differs.
 */
export const formatBytes = (bytes: number, language: LanguageTag): string => {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const formatted = new Intl.NumberFormat(language, {
    maximumFractionDigits: value < 10 && unitIndex > 0 ? 1 : 0,
  }).format(value);
  return `${formatted} ${units[unitIndex]}`;
};

export const formatDateTime = (iso: string, language: LanguageTag): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(language, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export const formatDate = (iso: string, language: LanguageTag): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(
    date,
  );
};

/** Formats milliseconds as m:ss for the recording timer. */
export const formatDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};
